<template>
  <div
    class="product-gallery"
    :class="`product-gallery--${responsiveLayout}`"
  >
    <div
      ref="scrollElement"
      class="product-gallery__main-container"
      @scroll="updateCurrentIndex"
    >
      <product-badge
        v-if="hasSpecialtyBadge && !isVideo"
        :product="product || liquid"
        badge-class="main-product__specialty-badge font-family-freight"
        badge-element="div"
        badge-type="specialtyBadge"
      />
      <ul
        v-if="(!isMobileHorizontalScroll && isMobile) || !isMobile"
        class="product-gallery-carousel__pagination splide__pagination splide__pagination--ttb"
      >
        <li
          v-for="(_, index) of filteredMedia"
          :key="index"
          role="presentation"
        >
          <button
            class="splide__pagination__page"
            :class="{
              [$class('active')]: currentIndex === index
            }"
            type="button"
            tabindex="-1"
            :aria-label="`Go to slide ${index + 1}`"
            @click="handleThumbnailClick(index)"
          />
        </li>
      </ul>

      <swiper-gallery-carousel
        class="product-gallery__main-carousel"
        :filtered-media="filteredMedia"
        :layout="responsiveLayout"
        :loading="loading"
        namespace="pdpGallery"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        type="main"
        :zoom="zoom"
        :product="product"
        :sizes="sizes"
        :show-mobile-arrows="showMobileArrows"
        @update-current-index="carouselIndexUpdated"
        @swiper-mounted="onSwiperMounted"
      />

      <a
        v-show="showOutfitAnchor"
        class="main-product__gallery-anchor"
        :class="stlClasses"
        href="#shop-the-look"
        :title="$string('lookbook.shop_the_look')"
        @click.prevent="smoothScrollToSection"
      >
        <icon-cart />

        <span
          class="text-body-2xs-regular main-product__gallery-anchor-label"
          v-text="$string('lookbook.shop_the_look')"
        />
      </a>
    </div>

    <div
      v-if="showThumbnails"
      class="product-gallery__thumbnails-container"
    >
      <product-gallery-thumbs
        class="product-gallery__thumbnails-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :config="config.thumbnails"
        :filtered-media="filteredMedia"
        :loading="loading"
        :product="product"
        @update-current-index="handleThumbnailClick"
        @thumbnail-clicked="goToSlide"
      />
    </div>

    <!-- Product gallery modal -->
    <teleport to="body">
      <overlay
        v-if="zoom"
        key="productGalleryModal"
        block-class="product-gallery-modal-overlay"
        direction="up"
        namespace="productGalleryModal"
        :show-overlay="false"
        :template="false"
        type="modal"
      >
        <template #body="overlay">
          <product-gallery-modal
            v-if="getComponentVisible('product-gallery-modal')"
            v-cloak
            :current-index="currentIndex"
            :current-namespace="currentNamespace"
            namespace="modal"
            :overlay="overlay"
            :product="product"
            :is-dickey-bundle="isDickeyBundle"
            @update-current-index="handleUpdateCurrentIndex"
            @update-current-namespace="handleUpdateCurrentNamespace"
            @gallery-overlay-closed="handleOverlayClosed"
          />
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {String} activeGroupName - Active swatch color name.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {String} layout - Product gallery layout, either `horizontal`,
 * `vertical`, `tabs`, or `grid`, supports a combination in the format
 * [mobile layout]-[desktop layout].
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapState } from 'vuex'

import ProductGalleryThumbs from '~async/product-gallery-thumbs/product-gallery-thumbs'
import SwiperGalleryCarousel from '~async/swiper-gallery-carousel/swiper-gallery-carousel'
import Overlay from '~global/overlay/overlay'
import IconCart from '~icons/general/cart.svg'
import ProductBadge from '~global/product-badge/product-badge'

import { values as breakpoints } from '~/config/breakpoints'

import { throttle } from '~/helpers/general'

export default {
  name: 'ProductGallery',

  components: {
    Overlay,
    SwiperGalleryCarousel,
    ProductGalleryThumbs,
    ProductBadge,
    IconCart,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    activeGroupName: {
      type: String,
      default: '',
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    layout: {
      type: String,
      default: 'horizontal',
      validator: (value) => {
        return value.split('-').every((part) => ['grid', 'horizontal', 'tabs', 'vertical'].includes(part))
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    zoom: {
      type: Boolean,
      default: true,
    },
    isDickeyBundle: {
      type: Boolean,
      default: false,
    },
    isMobileHorizontalScroll: {
      type: Boolean,
      default: false,
    },
    showMobileArrows: {
      type: Boolean,
      default: false,
    },
    hideGalleryVideo: {
      type: Boolean,
      default: false,
    },
    stlClasses: {
      type: Object,
      default: null,
    },
    showOutfitAnchor: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
    'update-is-video': (payload) => typeof payload === 'boolean',
    'gallery-overlay-closed': () => true,
  },

  data() {
    return {
      lockFreeScroll: false,
      mediaCount: 0,
      swiperRef: null,
      isVideo: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute carousel configs.
     * @returns {Object}
     */
    config() {
      let main = null
      if (this.currentNamespace === 'pdpGallery' || this.currentNamespace === 'gallery') {
        main = {
          arrows: true,
          mediaQuery: 'min',
          breakpoints: {
            1024: {
              width: '100%',
            },
          },
          keyboard: true,
          paginationKeyboard: true,
          perMove: 1,
          perPage: 1,
          slideFocus: true,
          type: 'slide',
          drag: 'free',
          ...this.getDynamicSplideConfigs(),
        }
      } else {
        main = {
          arrows: false,
          // Matches product media aspect ratio
          heightRatio: '1.256',
          keyboard: true,
          paginationKeyboard: true,
          perMove: 1,
          perPage: 1,
          releaseWheel: true,
          slideFocus: true,
          type: 'slide',
          wheelMinThreshold: 25,
          wheelSleep: this.$timing('quick'),
          drag: 'free',
          ...this.getDynamicSplideConfigs(),
        }
      }

      const thumbnails = {
        arrows: false,
        direction: 'ttb',
        drag: false,
        fixedHeight: '40px',
        focus: 1,
        height: `calc(40px * ${this.mediaLength})`,
        keyboard: true,
        perMove: this.mediaLength,
        perPage: this.mediaLength,
        type: 'slide',
      }

      /**
       * Return config.
       */
      return {
        main,
        thumbnails,
      }
    },

    /**
     * Only show active swatch's color, if exists, from media items.
     * @returns {Array}
     */
    filteredMedia() {
      if (this.isDickeyBundle) {
        return this.getDickeyBundleProductMedia()
      }

      if (!this.activeGroupName) {
        return this.product?.media || []
      }

      let filteredArray = this.product?.media.filter(mediaItem => {
        const shouldHideVideo = mediaItem.mediaType === 'video' && this.hideGalleryVideo
        return !shouldHideVideo && mediaItem?.alt?.includes(' - ')
      })

      // Reorder media to have video appear 2nd
      if (!this.hideGalleryVideo) {
        const videos = filteredArray.filter((mediaItem) => mediaItem.mediaType === 'video')
        const nonVideos = filteredArray.filter((mediaItem) => mediaItem.mediaType !== 'video')
        filteredArray = [
          nonVideos[0],
          ...videos,
          ...nonVideos.slice(1),
        ]
      }

      filteredArray.forEach((mediaItem, index) => {
        mediaItem.index = index
      })

      return filteredArray
    },

    /**
     * Length of filtered media array.
     * @returns {Number}
     */
    mediaLength() {
      return this.filteredMedia?.length
    },

    /**
     * Compute responsive layout based on mobile.
     * @returns {String}
     */
    responsiveLayout() {
      if (!this.layout.includes('-')) {
        return this.layout
      }

      return this.isMobile
        ? this.layout.split('-')[0]
        : this.layout.split('-')[1]
    },

    /**
     * Compute whether to show thumbnails carousel.
     * @returns {Boolean}
     */
    showThumbnails() {
      return (
        this.filteredMedia?.length > 1 &&
        (this.responsiveLayout === 'horizontal' || this.responsiveLayout === 'vertical')
      )
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`
    },

    /**
     * Compute if has secondary name.
     * @returns {Boolean}
     */
    hasSpecialtyBadge() {
      const productData = this.product || this.liquid
      return Boolean(productData?.metafields?.specialtyBadge)
    },
  },

  watch: {

    filteredMedia() {
      this.mediaCount = this.filteredMedia?.length
    },

    /**
     * Watch active group name update to reset scroll to start.
     */
    activeGroupName() {
      this.handleThumbnailClick(0, 'instant')
    },

    /**
     * Watch product to reset scroll to start.
     */
    product() {
      this.handleThumbnailClick(0, 'instant')
    },
  },

  mounted() {
    if (this.isDickeyBundle) {
      this.mediaCount = this.filteredMedia?.length
    }
  },

  methods: {

    onSwiperMounted(swiper) {
      this.swiperRef = swiper
    },

    goToSlide(index) {
      if (this.swiperRef) {
        this.swiperRef.slideTo(index)
      }
      // this.$refs.galleryCarousel?.splide?.go(index)
    },

    carouselIndexUpdated(newIndex) {
      this.isVideo = this.filteredMedia[newIndex]?.mediaType === 'video'
      this.handleUpdateCurrentIndex(newIndex)
      this.handleUpdateIsVideo(this.isVideo)
    },

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },

    /**
     * Emit update is video event.
     * @param {Boolean} isVideo True if the current slide is a video
     */
    handleUpdateIsVideo(isVideo) {
      this.$emit('update-is-video', isVideo)
    },

    /**
     * Scroll slide into view when a thumbnail is clicked
     * @param {Number} newIndex - Index of clicked thumbnail.
     * @param {String} [behavior] - Scrolling behaviour.
     */
    handleThumbnailClick(newIndex, behavior = 'smooth') {
      const scrollElement = this.$refs.scrollElement

      if (!scrollElement) {
        return
      }

      const scrollPosition = scrollElement.clientHeight * newIndex

      this.lockFreeScroll = true
      this.$emit('update-current-index', newIndex)

      this.$refs.scrollElement.scrollTo({
        left: 0,
        top: scrollPosition,
        behavior,
      })
      this.carouselIndexUpdated(newIndex)

      setTimeout(() => {
        this.lockFreeScroll = false
      }, this.$timing('slow'))
    },

    /**
     * Use correct image index method
     * @param event
     */
    updateCurrentIndex(event) {
      if (this.isDickeyBundle) {
        this.dickeyExperienceScroll(event)

        return
      }

      this.updateCurrentIndexFreeScroll(event)
    },

    /**
     * Calculate the nearest slide index when free scrolling gallery
     * @param {ScrollEvent} event - Scroll event
     */
    updateCurrentIndexFreeScroll(event) {
      if (this.lockFreeScroll) {
        return
      }
      throttle(() => {
        const height = event.target.clientHeight
        const scrollY = event.target.scrollTop

        if (height <= 0 || scrollY <= 0) {
          return
        }

        const index = Math.round(scrollY / height)

        this.$emit('update-current-index', index)
      }, this.$timing('normal'))()
    },

    /**
     * Get current image index for dickey bundle based on screen height
     * @param event
     */
    dickeyExperienceScroll(event) {
      if (this.lockFreeScroll) {
        return
      }

      throttle(() => {
        const splideElement = this.$refs.galleryCarousel?.$el
        const height = event.target?.clientHeight
        const scrollY = event.target?.scrollTop
        const splideHeight = splideElement?.clientHeight - height
        const singleSlideHeight = splideHeight / this.mediaCount
        const activeImageIndex = Math.floor(scrollY / singleSlideHeight)

        if (height <= 0 || scrollY <= 0) {
          return
        }

        const alterIndex =
          activeImageIndex < this.mediaCount ? activeImageIndex : this.mediaCount - 1

        this.$emit('update-current-index', alterIndex)
      }, this.$timing('normal'))()
    },

    /**
      * Emit close event on gallery modal close
      */
    handleOverlayClosed() {
      this.$emit('gallery-overlay-closed')
    },

    /**
     * Get first image as variant
     */
    getDickeyBundleProductMedia() {
      const variantImage = this.product?.selectedOrFirstAvailableVariant

      const filtered = this.product?.media.filter((media) => {
        return media?.image?.url !== variantImage?.image?.url
      })

      return [variantImage, ...filtered]
    },

    /**
     * Set dynamic splide configs
     */
    getDynamicSplideConfigs() {
      if (this.isMobile && this.isMobileHorizontalScroll) {
        return {
          direction: 'ltr',
          wheel: false,
          snap: true,
          destroy: false,
          pagination: false,
        }
      }

      return {
        direction: 'ltr',
        snap: false,
        destroy: false,
        pagination: false,
      }
    },

    /**
     * Scrolls to STL section with smooth behaviour.
     */
    smoothScrollToSection() {
      const targetSection = document.getElementById('shop-the-look')

      if (targetSection) {
        targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
</style>
